import React, { useEffect, useState } from 'react';
import { hospitalLegacyPhotos, LegacyPhoto } from '../data/hospitalLegacyPhotos';

const LegacyPhotoGallery = () => {
    const [activePhoto, setActivePhoto] = useState<LegacyPhoto | null>(null);
    const photos = hospitalLegacyPhotos;

    useEffect(() => {
        if (!activePhoto) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActivePhoto(null);
        };
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handler);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handler);
        };
    }, [activePhoto]);

    if (photos.length === 0) return null;

    return (
        <section className="relative w-full overflow-hidden bg-gradient-to-b from-transparent via-white/60 to-blue-50/40 px-3 py-12 sm:px-4 sm:py-16 md:py-20">
            <div className="relative mx-auto max-w-5xl">
                <div className="mb-8 text-center">
                    <span className="mb-3 inline-block rounded-full bg-red-500/10 px-3 py-1 font-redhat text-xs font-semibold uppercase tracking-wide text-red-700 sm:text-sm">
                        Heritage Gallery
                    </span>
                    <h2 className="font-ubuntu text-2xl font-bold text-lochmara-blue sm:text-3xl md:text-4xl">
                        Faces of Our Legacy
                    </h2>
                    <p className="mx-auto mt-3 max-w-2xl font-redhat text-sm text-gray-600 sm:text-base">
                        Founders, physicians, and moments that shaped The Great Emmanuel Hospital.
                    </p>
                    <p className="mt-2 font-redhat text-xs text-lochmara-blue md:hidden">
                        Tap any photo to view full size
                    </p>
                </div>

                {/* Single-column horizontal cards — full width on mobile & desktop */}
                <div className="flex flex-col gap-6 md:gap-8">
                    {photos.map((photo) => (
                        <button
                            key={photo.id}
                            type="button"
                            onClick={() => setActivePhoto(photo)}
                            className="group flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-md transition hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-lochmara-blue/40 md:flex-row"
                        >
                            {/* Horizontal image panel */}
                            <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-[#111827] md:aspect-auto md:w-[58%] md:min-h-[260px] lg:min-h-[300px]">
                                <img
                                    src={process.env.PUBLIC_URL + photo.src}
                                    alt={photo.title}
                                    loading="lazy"
                                    className="absolute inset-0 h-full w-full object-contain object-center"
                                />
                            </div>

                            {/* Caption panel */}
                            <div className="flex flex-1 flex-col justify-center p-4 sm:p-5 md:p-6 lg:p-8">
                                {photo.era && (
                                    <span className="mb-2 inline-block w-fit rounded-full bg-lochmara-blue/10 px-3 py-1 font-redhat text-[10px] font-bold uppercase tracking-wider text-lochmara-blue sm:text-xs">
                                        {photo.era}
                                    </span>
                                )}
                                <h3 className="font-ubuntu text-base font-bold leading-snug text-congress-blue sm:text-lg md:text-xl">
                                    {photo.title}
                                </h3>
                                <p className="mt-2 font-redhat text-sm leading-relaxed text-gray-600 md:text-base">
                                    {photo.caption}
                                </p>
                                <span className="mt-4 inline-flex w-fit items-center gap-1.5 font-redhat text-xs font-semibold text-lochmara-blue md:text-sm">
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
                                    </svg>
                                    View full photo
                                </span>
                            </div>
                        </button>
                    ))}
                </div>

                {photos.length < 7 && (
                    <p className="mt-8 text-center font-redhat text-xs text-gray-500 sm:text-sm">
                        More heritage photographs will be added as our archive is digitized.
                    </p>
                )}
            </div>

            {activePhoto && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-3 sm:p-6"
                    onClick={() => setActivePhoto(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={activePhoto.title}
                >
                    <div
                        className="relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setActivePhoto(null)}
                            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-2xl text-white"
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <div className="flex items-center justify-center bg-[#111827] p-2">
                            <img
                                src={process.env.PUBLIC_URL + activePhoto.src}
                                alt={activePhoto.title}
                                className="max-h-[65vh] w-full object-contain sm:max-h-[75vh]"
                            />
                        </div>
                        <div className="overflow-y-auto border-t border-gray-100 p-4 sm:p-6">
                            {activePhoto.era && (
                                <span className="mb-2 inline-block rounded-full bg-lochmara-blue/10 px-3 py-1 font-redhat text-xs font-bold uppercase tracking-wide text-lochmara-blue">
                                    {activePhoto.era}
                                </span>
                            )}
                            <h3 className="font-ubuntu text-lg font-bold text-congress-blue sm:text-2xl">{activePhoto.title}</h3>
                            <p className="mt-2 font-redhat text-sm text-gray-600 sm:text-base">{activePhoto.caption}</p>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default LegacyPhotoGallery;
